import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import {
  X,
  CheckCircle,
  Target,
  DollarSign,
  Clock,
  TrendingUp,
  Phone,
  Calendar,
  Sparkles,
  ArrowRight,
  Zap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WelcomeModal = ({ onClose }) => {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    pendingTasks: 0,
    leadsNeedingFollowUp: 0,
    hotLeads: 0,
    potentialCommission: 0,
    totalLeads: 0,
    closedWon: 0
  });
  const [loading, setLoading] = useState(true);
  const [topLeads, setTopLeads] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch tasks
        const tasksRes = await axios.get(`${API_URL}/api/tasks`, getAuthHeaders());
        const pendingTasks = tasksRes.data.filter(t => t.status === 'pending').length;

        // Fetch leads
        const leadsRes = await axios.get(`${API_URL}/api/leads`, getAuthHeaders());
        const leads = leadsRes.data;
        
        // Calculate stats
        const hotLeads = leads.filter(l => l.ai_score >= 70).length;
        const needsFollowUp = leads.filter(l => {
          if (!l.last_contact_date) return true;
          const lastContact = new Date(l.last_contact_date);
          const daysSince = (new Date() - lastContact) / (1000 * 60 * 60 * 24);
          return daysSince > 7;
        }).length;
        
        const closedWon = leads.filter(l => l.status === 'closed_won').length;
        
        // Estimate potential commission (demo calculation)
        const pipelineLeads = leads.filter(l => 
          ['qualified', 'demo_scheduled', 'demo_requested', 'follow_up'].includes(l.status)
        );
        const potentialCommission = pipelineLeads.length * 1500; // Avg commission per deal

        // Get top leads to follow up
        const topLeadsToShow = leads
          .filter(l => l.status !== 'closed_won' && l.status !== 'closed_lost')
          .sort((a, b) => (b.ai_score || 0) - (a.ai_score || 0))
          .slice(0, 3);

        setStats({
          pendingTasks,
          leadsNeedingFollowUp: needsFollowUp,
          hotLeads,
          potentialCommission,
          totalLeads: leads.length,
          closedWon
        });
        setTopLeads(topLeadsToShow);
      } catch (error) {
        console.error('Error fetching welcome data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [getAuthHeaders]);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const getScoreColor = (score) => {
    if (score >= 70) return { bg: 'bg-green-100', text: 'text-green-700', label: 'Hot' };
    if (score >= 40) return { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Warm' };
    return { bg: 'bg-slate-100', text: 'text-slate-600', label: 'Cold' };
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          data-testid="welcome-modal"
        >
          {/* Header with gradient */}
          <div className="bg-gradient-to-r from-[#1B365D] to-[#0071E3] p-6 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
            
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors z-10 p-2 rounded-lg hover:bg-white/10"
              data-testid="close-welcome-modal"
            >
              <X className="w-6 h-6" />
            </button>
            
            <div className="relative">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-5 h-5 text-yellow-300" />
                <span className="text-white/80 text-sm">Daily Briefing</span>
              </div>
              <h2 className="text-2xl font-bold mb-1">
                {getGreeting()}, {user?.name?.split(' ')[0] || 'Agent'}!
              </h2>
              <p className="text-white/80">
                Here's what's waiting for you today
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6 max-h-[60vh] overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-8 h-8 border-2 border-[#1B365D]/20 border-t-[#1B365D] rounded-full animate-spin" />
              </div>
            ) : (
              <>
                {/* Quick Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Card className="border-0 bg-amber-50 shadow-sm">
                    <CardContent className="p-4 text-center">
                      <Clock className="w-6 h-6 text-amber-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-amber-700">{stats.pendingTasks}</p>
                      <p className="text-xs text-amber-600">Pending Tasks</p>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-0 bg-blue-50 shadow-sm">
                    <CardContent className="p-4 text-center">
                      <Phone className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-blue-700">{stats.leadsNeedingFollowUp}</p>
                      <p className="text-xs text-blue-600">Need Follow-up</p>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-0 bg-green-50 shadow-sm">
                    <CardContent className="p-4 text-center">
                      <Target className="w-6 h-6 text-green-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-green-700">{stats.hotLeads}</p>
                      <p className="text-xs text-green-600">Hot Leads</p>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-0 bg-purple-50 shadow-sm">
                    <CardContent className="p-4 text-center">
                      <DollarSign className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-purple-700">${stats.potentialCommission.toLocaleString()}</p>
                      <p className="text-xs text-purple-600">Pipeline Value</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Commission Incentive Banner */}
                {stats.potentialCommission > 0 && (
                  <Card className="border-0 bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg overflow-hidden">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                          <TrendingUp className="w-6 h-6" />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-lg">Your potential earnings this month</p>
                          <p className="text-white/90 text-sm">
                            You have {stats.totalLeads - stats.closedWon} active leads in your pipeline. 
                            Close them to earn up to <span className="font-bold">${stats.potentialCommission.toLocaleString()}</span> in commissions!
                          </p>
                        </div>
                        <Zap className="w-8 h-8 text-yellow-300" />
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Top Leads to Follow Up */}
                {topLeads.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <Target className="w-4 h-4 text-[#1B365D]" />
                      Priority Leads to Contact Today
                    </h3>
                    <div className="space-y-2">
                      {topLeads.map((lead) => {
                        const scoreInfo = getScoreColor(lead.ai_score || 0);
                        return (
                          <Card 
                            key={lead.id} 
                            className="border border-slate-100 hover:border-[#0071E3] transition-colors cursor-pointer"
                            onClick={() => {
                              onClose();
                              navigate(`/leads/${lead.id}`);
                            }}
                          >
                            <CardContent className="p-3 flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-[#1B365D]/10 flex items-center justify-center">
                                  <span className="text-[#1B365D] font-semibold text-sm">
                                    {lead.organization_name?.charAt(0) || 'L'}
                                  </span>
                                </div>
                                <div>
                                  <p className="font-medium text-slate-900">{lead.organization_name}</p>
                                  <p className="text-sm text-slate-500">{lead.contact_name}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {lead.ai_score !== undefined && (
                                  <Badge className={`${scoreInfo.bg} ${scoreInfo.text} border-0`}>
                                    {lead.ai_score} - {scoreInfo.label}
                                  </Badge>
                                )}
                                <ArrowRight className="w-4 h-4 text-slate-400" />
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Motivational Tip */}
                <Card className="border-0 bg-slate-50">
                  <CardContent className="p-4 flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#1B365D]/10 flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-4 h-4 text-[#1B365D]" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Pro Tip</p>
                      <p className="text-sm text-slate-600">
                        Following up within 24 hours of a demo request increases close rates by 50%. 
                        Check your hot leads and reach out today!
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>

          {/* Footer Actions */}
          <div className="p-6 pt-0 flex flex-col sm:flex-row gap-3">
            <Button
              variant="outline"
              className="flex-1 rounded-full"
              onClick={() => {
                onClose();
                navigate('/activities');
              }}
              data-testid="view-tasks-btn"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              View Tasks ({stats.pendingTasks})
            </Button>
            <Button
              className="flex-1 bg-[#1B365D] hover:bg-[#1B365D]/90 rounded-full"
              onClick={() => {
                onClose();
                navigate('/leads');
              }}
              data-testid="go-to-leads-btn"
            >
              <Target className="w-4 h-4 mr-2" />
              Start Working Leads
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default WelcomeModal;
